import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import toast from "react-hot-toast";
import RegistrationCheckForm from "../components/home/FormTemplate";
import { useApiMutation } from "../hooks/useMutation";
import { usePollingLocations } from "../hooks/usePollingLocations";
import MapIcon from '@mui/icons-material/Map';
import { ArrowBack } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";

// Fix Leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MapController = ({ center, zoom }: { center: [number, number]; zoom: number }) => {
  const map = useMap();

  React.useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);

  return null;
};

interface FormData {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  phone?: string;
  email: string;
  dob: string;
  gender: string;
  reminder: boolean;
}

export default function PollingLocation() {
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [searchParams] = useSearchParams();
  const isInIframe = window.self !== window.top;
  const embedIdFromPage = searchParams.get("embed_id") || window.sessionStorage.getItem("embedded_partner_id") || "";
  const isEmbeddedFlow = isInIframe || searchParams.get("embed") === "true" || !!embedIdFromPage;

  const { data: pollingLocationsData, isLoading: isPollingLoading, error: pollingError, refetch: refetchPollingLocations } = usePollingLocations({
    address: formData?.address || "",
    city: formData?.city || "",
    state: formData?.state || "",
    zipcode: formData?.zipcode || "",
    headers: isEmbeddedFlow && embedIdFromPage ? { "x-embed-id": embedIdFromPage } : undefined,
    enabled: showResults && !!(formData?.address && formData?.city && formData?.state && formData?.zipcode),
  });

  const pollingLocations = pollingLocationsData?.data?.pollingLocations || [];

  const handleFormSubmit = (data: FormData) => {
    setFormData(data);
    setShowResults(true);
  };

  const handleBackToForm = () => {
    setShowResults(false);
    setFormData(null);
  };

  return (
    <div className="min-h-screen flex flex-col items-center pt-12 pb-12 px-2"
      style={{
        background: "linear-gradient(135deg, #e0e7ff 0%, #fafafa 80%)",
        minHeight: "100vh",
      }}
    >

       {isInIframe && <div className="flex items-center gap-2 cursor-pointer absolute top-5 left-5  text-sm" onClick={() => window.history.back()}>
        <ArrowBack /> Go back
      </div>}


      <div className="w-full max-w-4xl mx-auto">
        {!showResults ? (
          <div className="space-y-8">
            <div className="text-center">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Find Your Polling Location
              </h1>
              <p className="text- text-gray-600 mb-4">
                Enter your information to locate your polling place
              </p>
            </div>
            <RegistrationCheckForm 
              header="Polling Location" 
              onSubmit={handleFormSubmit}
            />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-600 font-medium">Polling Location Results</span>
              </div>
              <button
                onClick={handleBackToForm}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                Back to Form
              </button>
            </div>

            {/* Polling Location Results */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl text-gray-900 font-semibold mb-2">Your Polling Location</h2>
              <p className="text-gray-600 text-sm mb-6">
                Access official polling location information for your address.
              </p>

              <div className="mb-6">
                <div className="h-96 z-[99] rounded-lg overflow-hidden border border-gray-300">
                  <MapContainer
                    center={pollingLocations.length > 0 ? [pollingLocations[0].latitude, pollingLocations[0].longitude] : [30.2672, -97.7431]}
                    zoom={pollingLocations.length > 0 ? 14 : 12}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <MapController
                      center={pollingLocations.length > 0 ? [pollingLocations[0].latitude, pollingLocations[0].longitude] : [30.2672, -97.7431]}
                      zoom={pollingLocations.length > 0 ? 14 : 12}
                    />
                    {pollingLocations.map((location, index) => (
                      <Marker key={index} position={[location.latitude, location.longitude]}>
                        <Popup>
                          <div className="p-2">
                            <h3 className="font-semibold text-sm">{location.address.locationName}</h3>
                            <p className="text-xs text-gray-600">
                              {location.address.line1}
                              {location.address.line2 && <br />}
                              {location.address.city}, {location.address.state} {location.address.zip}
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </div>

              {/* Polling Locations List */}
              <div className="space-y-4">
                {isPollingLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Finding polling locations...</span>
                  </div>
                ) : pollingError ? (
                  <div className="text-center py-8">
                    <MapIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-600 mb-3">Error finding polling locations.</p>
                    <button
                      onClick={() => refetchPollingLocations()}
                      className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Try Again
                    </button>
                  </div>
                ) : pollingLocations.length > 0 ? (
                  pollingLocations.map((location, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900 mb-2">
                            {location.address.locationName}
                          </h3>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>{location.address.line1}</p>
                            {location.address.line2 && <p>{location.address.line2}</p>}
                            <p>
                              {location.address.city}, {location.address.state} {location.address.zip}
                            </p>
                            {location.pollingHours && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <p className="font-medium text-gray-700">Hours:</p>
                                <p className="text-xs whitespace-pre-line">{location.pollingHours}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                            Polling Place
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <MapIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-600">No polling locations found for your address.</p>
                    <button
                      onClick={() => refetchPollingLocations()}
                      className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Try Again
                    </button>
                     {pollingLocationsData?.data?.state?.[0]?.electionAdministrationBody?.votingLocationFinderUrl && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800 mb-2">
                          For more polling location results, visit your state's official website:
                        </p>
                        <a
                          href={pollingLocationsData.data.state[0].electionAdministrationBody.votingLocationFinderUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
                        >
                          {pollingLocationsData.data.state[0].electionAdministrationBody.votingLocationFinderUrl}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  onClick={handleBackToForm}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={() => refetchPollingLocations()}
                  disabled={isPollingLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isPollingLoading ? "Refreshing..." : "Refresh Locations"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
